import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { EvidenceGallery } from '@/components/EvidenceGallery';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { roleLabel } from '@/i18n/roleLabel';

/**
 * The one-page return story (Part B, "real differentiator"): original
 * order/shipment, what came back, condition, disposition, who decided,
 * timestamp, post-disposition movement, evidence. Never presented as a
 * generic inventory adjustment.
 *
 * REGULATED EVIDENCE epic (ADR-074) UI wiring: the old bottom-of-page
 * "Evidence" card was a placeholder ("not implemented, out of scope") —
 * photo evidence is real now, so it is replaced with a real, per-record
 * `EvidenceGallery`: one per received line (`entityKind: 'INBOUND_LINE'`,
 * the physical condition photo) and one per disposition decision
 * (`entityKind: 'RETURN_DISPOSITION'`, the decision's own supporting
 * photo) — matching `evidence.ts`'s actual entity-linkage contract, not a
 * single page-wide bucket.
 */
export function ReturnDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi(() => endpoints.returnDetail(id!), [id]);
  const { hasPermission } = useAuth();
  const canReadEvidence = hasPermission('EVIDENCE.READ');
  const [expandedLine, setExpandedLine] = useState<string | null>(null);
  const [expandedDisposition, setExpandedDisposition] = useState<string | null>(null);
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { return: header, lines, dispositions } = result.data;
  const r = header as Record<string, unknown>;
  const ownerId = String(r.owner_id);

  return (
    <div>
      <div className="page-header">
        <h1>{t('returnDetail.title', { number: String(r.number) })}</h1>
        <StatusBadge value={String(r.state)} />
      </div>

      <div className="card">
        <dl className="kv">
          <dt>{t('inbounds.colSeller')}</dt>
          <dd>{String(r.owner_name)}</dd>
          <dt>{t('inboundDetail.arrived')}</dt>
          <dd>{r.arrived_at ? formatDateTime(String(r.arrived_at), locale) : '—'}</dd>
        </dl>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('returnDetail.whatCameBack')}</h2>
        {lines.length === 0 ? (
          <p>{t('inboundDetail.noLines')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventory.colSku')}</th>
                <th>{t('returnDetail.colQtyReceived')}</th>
                <th>{t('returnDetail.colCondition')}</th>
                <th>{t('inboundDetail.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((row) => {
                const l = row as Record<string, unknown>;
                const lineId = String(l.id);
                return (
                  <Fragment key={lineId}>
                    <tr>
                      <td>{l.sku_code ? `${String(l.sku_code)} — ${String(l.sku_name)}` : '—'}</td>
                      <td>{formatNumber(String(l.qty_received), locale)}</td>
                      <td>
                        <StatusBadge value={String(l.discrepancy)} />
                        {l.discrepancy_note ? <div className="sub">{String(l.discrepancy_note)}</div> : null}
                      </td>
                      <td>
                        <button type="button" onClick={() => setExpandedLine(expandedLine === lineId ? null : lineId)}>
                          {expandedLine === lineId ? t('common.hide') : t('inboundDetail.photos')}
                        </button>
                      </td>
                    </tr>
                    {expandedLine === lineId && (
                      <tr>
                        <td colSpan={4}>
                          <EvidenceGallery
                            entityKind="INBOUND_LINE"
                            entityId={lineId}
                            ownerId={ownerId}
                            skuId={l.sku_id ? String(l.sku_id) : null}
                            canRead={canReadEvidence}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('returnDetail.dispositionDecisions')}</h2>
        {dispositions.length === 0 ? (
          <p>{t('returnDetail.awaitingDisposition')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inboundDetail.colQty')}</th>
                <th>{t('inboundDetail.colDisposition')}</th>
                <th>{t('returnDetail.colDecidedBy')}</th>
                <th>{t('returnDetail.colTimestamp')}</th>
                <th>{t('returnDetail.colPostLocation')}</th>
                <th>{t('inboundDetail.colReversed')}</th>
                <th>{t('inboundDetail.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {dispositions.map((row) => {
                const d = row as Record<string, unknown>;
                const dispositionId = String(d.id);
                return (
                  <Fragment key={dispositionId}>
                    <tr>
                      <td>{formatNumber(String(d.qty), locale)}</td>
                      <td>
                        <StatusBadge value={String(d.disposition)} />
                      </td>
                      <td>{d.decided_by_role ? roleLabel(t, String(d.decided_by_role)) : '—'}</td>
                      <td>{formatDateTime(String(d.created_at), locale)}</td>
                      <td>{d.to_location_code ? String(d.to_location_code) : '—'}</td>
                      <td>{d.reversed_at ? formatDateTime(String(d.reversed_at), locale) : '—'}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setExpandedDisposition(expandedDisposition === dispositionId ? null : dispositionId)}
                        >
                          {expandedDisposition === dispositionId ? t('common.hide') : t('inboundDetail.photos')}
                        </button>
                      </td>
                    </tr>
                    {expandedDisposition === dispositionId && (
                      <tr>
                        <td colSpan={7}>
                          <EvidenceGallery entityKind="RETURN_DISPOSITION" entityId={dispositionId} ownerId={ownerId} canRead={canReadEvidence} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
